import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  isDangerous?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  isDangerous = false,
  onConfirm,
  onCancel,
  isLoading = false,
}) => {
  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      throw error;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#111",
          color: "#fff",
          border: "1px solid #333",
          margin: { xs: "16px", sm: "32px" },
          width: { xs: "calc(100% - 32px)", sm: "auto" },
        },
      }}
    >
      <DialogTitle sx={{ color: isDangerous ? "#f44336" : "hsl(19 101% 51% / 1)" }}>
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {typeof message === "string" ? (
          <Typography sx={{ color: "#ccc", mt: 1, whiteSpace: "pre-line", lineHeight: 1.7 }}>
            {message}
          </Typography>
        ) : (
          message
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onCancel} 
          sx={{ color: "#fff" }}
          disabled={isLoading}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : undefined}
          sx={{
            backgroundColor: isDangerous ? "#f44336" : "hsl(19 101% 51% / 1)",
            "&:hover": {
              backgroundColor: isDangerous ? "#d32f2f" : "hsl(19 101% 41% / 1)",
            },
            "&:disabled": {
              backgroundColor: isDangerous 
                ? "#f44336a0" 
                : "hsl(19 101% 51% / 0.4)",
              color: "#fff",
            },
          }}
        >
          {isLoading ? "Processing..." : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
